import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FlexBox, FlexBoxDirection, Input, Label } from '@ui5/webcomponents-react';
import { SaveResult, SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import { useTranslation } from 'react-i18next';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { useStyles } from '../CreateQueryView/CreateQueryView.jss';
import { IssueType, useCreateMarketingMutation } from 'generated/graphql';
import isMatch from 'lodash/isMatch';
import { useMarketingQueryStore } from 'stores/CreateMarketingQueryStore';
import { commonMarketingQueryInitialStateFields } from 'stores/slices/MarketingQuerySlices.ts/CommonMarketingQuerySlice';
import dayjs from 'dayjs';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';

export const MarketingQuery = forwardRef<SpecificQueryType>((_, ref) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const store = useMarketingQueryStore();
  const freezeStore = useCreateQueryFreezeStore();
  const baseQueryStore = useBaseQueryStore();
  const {
    deadline,
    details,
    projectManager,
    selectedIssueType,
    selectedProject,
    sourceText,
    targetLanguage,
    targetText,
    attachments
  } = baseQueryStore;

  /** GraphQL Queries/Mutations */
  const [{ fetching }, callCreateQuery] = useCreateMarketingMutation();

  useImperativeHandle(ref, () => ({
    save: () => {
      return onSave();
    },
    cancel: () => {
      store.resetData();
    },
    isValid: () => {
      return true;
    },
    containsRelevantChanges: () => {
      return containsRelevantChanges();
    }
  }));

  useEffect(() => {
    store.setFetching(fetching);

    return () => store.setFetching(false);
  }, [fetching]);

  const onSave = async (): Promise<SaveResult> => {
    const result = await callCreateQuery({
      input: {
        deadline: dayjs(deadline).format('YYYY-MM-DD'),
        issueType: selectedIssueType as IssueType,
        project: selectedProject,
        sourceText: sourceText,
        targetLanguage: targetLanguage,
        details: details,
        pmName: projectManager,
        targetText: targetText,
        fileName: store.fileName
      },
      files: attachments
    });
    const success = !result.error;
    if (success) {
      store.resetData();
      return { id: result?.data?.query.id };
    }
    return { error: result.error };
  };

  const containsRelevantChanges = () => {
    return !isMatch(store, commonMarketingQueryInitialStateFields);
  };

  const onChangeFileName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setFileName(value);
  };

  return (
    <>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="file-name-label" className={classes.label} showColon wrappingType="None">
            <span data-help-id="create-query-Marketing-file-name">{t('file-name-label')}</span>
          </Label>
          <Input
            aria-labelledby="file-name-label"
            onChange={onChangeFileName}
            className={classes.formLineInput}
            value={store.fileName}
            disabled={freezeStore.fileName}
          ></Input>
        </div>
      </FlexBox>
    </>
  );
});
